import React, { createContext, useContext, useMemo, ReactNode } from "react";
import useLocalStorage from "../hooks/useLocalStorage";

export type ViewMode = "home" | "student_life" | "academics" | "campus" | "marketplace";

const VIEW_MODE_STORAGE_KEY = "gedoba-view-mode";

interface ViewModeContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  isHome: boolean;
  isStudentLife: boolean;
  isAcademics: boolean;
  isCampus: boolean;
  isMarketplace: boolean;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(
  undefined,
);

interface ViewModeProviderProps {
  children: ReactNode;
}

export function ViewModeProvider({ children }: ViewModeProviderProps) {
  const [viewMode, setStored] = useLocalStorage<ViewMode>(
    VIEW_MODE_STORAGE_KEY,
    "home",
  );

  const value: ViewModeContextType = useMemo(
    () => ({
      viewMode,
      setViewMode: setStored,
      isHome: viewMode === "home",
      isStudentLife: viewMode === "student_life",
      isAcademics: viewMode === "academics",
      isCampus: viewMode === "campus",
      isMarketplace: viewMode === "marketplace",
    }),
    [viewMode, setStored],
  );

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode(): ViewModeContextType {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error("useViewMode must be used within a ViewModeProvider");
  }
  return context;
}
